import React, { useState, useEffect } from "react"
import { getJson, deleteJson } from "../../helpers/http"
import { API_ENDPOINTS } from "../../helpers/api"
import { hasPremiumAccess, authToggleLabel } from "../../helpers/auth"
import usePurchaseRedirect from "../../hooks/usePurchaseRedirect"
import AuthForms from "../shared/AuthForms"
import UserBar from "../shared/UserBar"
import PremiumPassModal, { FEATURES } from "../shared/PremiumPassModal"
import { AccountCrossSiteNote, PremiumCrossSiteNote } from "../shared/CrossSiteNote"
import { NYP_SITE, RODG_SITE } from "../../helpers/sites"

const RODG_FEATURES = ["ai_planning", "route", "wait_charts", "crowd_calendar", "requests"].map((k) =>
  FEATURES.find((f) => f.key === k)
)

const SESSION_CACHE_KEY = "rodg_session_user"

const getCachedUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_CACHE_KEY))
  } catch {
    return null
  }
}

const cacheUser = (user) => {
  if (user) sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(user))
  else sessionStorage.removeItem(SESSION_CACHE_KEY)
}

const dispatchAuthChange = (user) =>
  document.dispatchEvent(
    new CustomEvent("rodg-auth-change", {
      detail: { loggedIn: !!user, hasPremium: hasPremiumAccess(user) },
    })
  )

const Auth = () => {
  const [currentUser, setCurrentUser] = useState(getCachedUser)
  const [showForms, setShowForms] = useState(false)
  const [authMode, setAuthMode] = useState("login")
  const [loading, setLoading] = useState(!currentUser)
  const [showPremium, setShowPremium] = useState(false)

  const updateUser = (user) => {
    setCurrentUser(user)
    cacheUser(user)
  }

  const refreshSession = ({ notify = false } = {}) =>
    getJson(API_ENDPOINTS.SESSION).then(({ data }) => {
      const user = data?.user || null
      updateUser(user)
      if (notify) dispatchAuthChange(user)
    })

  const { purchaseSuccess, clearPurchaseSuccess } = usePurchaseRedirect(() => setShowPremium(true))

  useEffect(() => {
    refreshSession({ notify: true }).then(() => setLoading(false))

    const handleOpenPremium = () => setShowPremium(true)
    const handleOpenSignup = () => {
      setShowForms(true)
      setAuthMode("signup")
    }
    document.addEventListener("rodg-open-premium", handleOpenPremium)
    document.addEventListener("rodg-open-signup", handleOpenSignup)
    return () => {
      document.removeEventListener("rodg-open-premium", handleOpenPremium)
      document.removeEventListener("rodg-open-signup", handleOpenSignup)
    }
  }, [])

  const handleLogin = (user) => {
    updateUser(user)
    setShowForms(false)
    dispatchAuthChange(user)
  }

  const handleLogout = async () => {
    await deleteJson(API_ENDPOINTS.LOGOUT)
    updateUser(null)
    dispatchAuthChange(null)
  }

  if (loading) return null

  if (currentUser) {
    return (
      <div className="rodg-auth">
        <UserBar user={currentUser} onPremiumClick={() => setShowPremium(true)} onLogout={handleLogout} />
        {showPremium && (
          <PremiumPassModal
            onClose={() => {
              setShowPremium(false)
              clearPurchaseSuccess()
              refreshSession({ notify: true })
            }}
            purchaseSuccess={purchaseSuccess}
            features={RODG_FEATURES}
            crossSiteNote={<PremiumCrossSiteNote primarySite={RODG_SITE} secondarySite={NYP_SITE} />}
          />
        )}
      </div>
    )
  }

  return (
    <div className="rodg-auth">
      <button className="rodg-auth-toggle" onClick={() => setShowForms((show) => !show)}>
        {authToggleLabel(showForms, authMode)}
      </button>
      {showForms && (
        <AuthForms
          onLogin={handleLogin}
          mode={authMode}
          onModeChange={setAuthMode}
          supportEmail="support@rideordieguide.com"
          crossSiteNote={<AccountCrossSiteNote primarySite={RODG_SITE} secondarySite={NYP_SITE} />}
        />
      )}
    </div>
  )
}

export default Auth
